<template>
  <div class="adjust-wb" :class="{ 'is-panel-only': hideList }">
    <div class="wb-body">
      <section v-if="!hideList" class="wb-list-pane" :class="{ 'is-out': listTab === 'out' }">
        <div class="wb-tabs">
          <button
            type="button"
            class="wb-tab"
            :class="{ on: listTab === 'in' }"
            @click="switchTab('in')"
          >
            수입
            <em v-if="pendingIncomes.length">{{ pendingIncomes.length }}</em>
          </button>
          <button
            type="button"
            class="wb-tab"
            :class="{ on: listTab === 'out' }"
            @click="switchTab('out')"
          >
            지출
            <em v-if="pendingItems.length">{{ pendingItems.length }}</em>
          </button>
        </div>

        <div v-show="listTab === 'in'" class="wb-rows">
          <div class="wb-cols income">
            <span>일자</span>
            <span>적요</span>
            <span>금액</span>
            <span class="is-in">계정</span>
          </div>
          <p v-if="incomeRows.length === 0" class="wb-empty">수입 내역이 없습니다</p>
          <button
            v-for="item in incomeRows"
            :key="item.cb_idx"
            type="button"
            class="wb-row income"
            :class="{
              picked: isIncomePicked(item),
              closed: !isEditable(item),
            }"
            :disabled="!isEditable(item)"
            @click="toggleIncomeRow(item)"
          >
            <span class="wb-r-date">{{ formatDate(item.cb_date) }}</span>
            <span class="wb-r-memo">{{ item.cb_memo || '-' }}</span>
            <span class="wb-r-price">{{ formatNumber(item.cb_price) }}</span>
            <span class="wb-r-acc is-in" :class="{ miss: !hasScode(item) }">
              {{ hasScode(item) ? incomeName(item.cb_scode) : '미지정' }}
            </span>
          </button>
        </div>

        <div v-show="listTab === 'out'" class="wb-rows">
          <div class="wb-cols expense">
            <span>일자</span>
            <span>적요</span>
            <span>금액</span>
            <span class="is-out">지출계정</span>
            <span class="is-in">상대계정</span>
          </div>
          <p v-if="expenseRows.length === 0" class="wb-empty">지출 내역이 없습니다</p>
          <button
            v-for="item in expenseRows"
            :key="item.cb_idx"
            type="button"
            class="wb-row expense"
            :class="{
              picked: isPicked(item),
              closed: !isEditable(item),
            }"
            :disabled="!isEditable(item)"
            @click="toggleRow(item)"
          >
            <span class="wb-r-date">{{ formatDate(item.cb_date) }}</span>
            <span class="wb-r-memo">{{ item.cb_memo || '-' }}</span>
            <span class="wb-r-price">{{ formatNumber(item.cb_price) }}</span>
            <span class="wb-r-acc is-out" :class="{ miss: !hasScode(item) }">
              {{ hasScode(item) ? expenseName(item.cb_scode) : '미지정' }}
            </span>
            <span class="wb-r-acc is-in" :class="{ miss: !hasRel(item) }">
              {{ hasRel(item) ? incomeName(item.cb_rel_acc) : '미지정' }}
            </span>
          </button>
        </div>
      </section>

      <aside class="wb-acc-pane">
        <!-- 수입: 계정 탭에서만 -->
        <template v-if="listTab === 'in' && adjustFocus === 'account'">
          <div class="wb-acc-head">
            <span class="is-in">수입 계정</span>
            <div class="wb-acc-head-tools">
              <input v-model="incomeSearchQuery" type="text" class="wb-acc-search" placeholder="검색" />
              <button
                type="button"
                class="wb-gear"
                data-tour="bb-test-recommend-settings"
                @click="emit('open-match', 'account')"
              >자동화 적용</button>
            </div>
          </div>
          <p v-if="!selectedIncomeCount" class="wb-acc-hint">왼쪽에서 수입 항목을 고르세요</p>
          <p v-else-if="recommendLoading" class="wb-acc-hint soft">예전에 쓴 계정 찾는 중…</p>
          <div v-if="selectedIncomeCount" class="wb-acc-list income">
            <template v-if="rankedIncomeCodes.current.length">
              <p class="wb-acc-sec">현재</p>
              <button
                v-for="code in rankedIncomeCodes.current"
                :key="`cur-in-${code.scode}`"
                type="button"
                class="wb-acc-btn current"
                :class="{ on: draftIncome === code.scode }"
                title="현재 적용된 계정"
                @click="onPickIncome(code.scode)"
              >
                <span class="wb-acc-btn-label">{{ code.sname }}</span>
                <em class="wb-cur-tag">현재</em>
              </button>
            </template>

            <template v-if="recommendSettings.enabled">
              <p class="wb-acc-sec">추천</p>
              <p
                v-if="!recommendLoading && !rankedIncomeCodes.recommended.length"
                class="wb-acc-empty"
              >추천 없음</p>
              <button
                v-for="code in rankedIncomeCodes.recommended"
                :key="`rec-in-${code.scode}`"
                type="button"
                class="wb-acc-btn recommend"
                :class="{ on: draftIncome === code.scode }"
                :title="code.reason || '적요 패턴 추천'"
                @click="onPickIncome(code.scode)"
              >
                <span class="wb-acc-btn-label">{{ code.sname }}</span>
                <em class="wb-rec-tag">추천</em>
              </button>
            </template>

            <p class="wb-acc-sec">전체</p>
            <button
              v-for="code in rankedIncomeCodes.rest"
              :key="code.scode"
              type="button"
              class="wb-acc-btn"
              :class="{ on: draftIncome === code.scode }"
              @click="onPickIncome(code.scode)"
            >{{ code.sname }}</button>
          </div>
        </template>

        <!-- 지출 · 계정만 -->
        <template v-else-if="listTab === 'out' && adjustFocus === 'account'">
          <div class="wb-acc-head">
            <span class="is-out">지출 계정</span>
            <div class="wb-acc-head-tools">
              <input v-model="searchQuery" type="text" class="wb-acc-search" placeholder="검색" />
              <button
                type="button"
                class="wb-gear"
                data-tour="bb-test-recommend-settings"
                @click="emit('open-match', 'account')"
              >자동화 적용</button>
            </div>
          </div>
          <p v-if="!selectedCount" class="wb-acc-hint">왼쪽에서 지출 항목을 고르세요</p>
          <p v-else-if="recommendLoading && recommendSettings.enabled" class="wb-acc-hint soft">예전에 쓴 계정 찾는 중…</p>
          <div v-if="selectedCount" class="wb-acc-list expense">
            <template v-if="rankedExpenseCodes.current.length">
              <p class="wb-acc-sec">현재</p>
              <button
                v-for="code in rankedExpenseCodes.current"
                :key="`cur-out-${code.scode}`"
                type="button"
                class="wb-acc-btn current"
                :class="{ on: draftExpense === code.scode }"
                title="현재 적용된 계정"
                @click="pickExpense(code.scode)"
              >
                <span class="wb-acc-btn-label">{{ code.sname }}</span>
                <em class="wb-cur-tag">현재</em>
              </button>
            </template>

            <template v-if="showExpenseRecommendSection">
              <p class="wb-acc-sec">추천</p>
              <p
                v-if="!recommendLoading && !rankedExpenseCodes.recommended.length"
                class="wb-acc-empty"
              >추천 없음</p>
              <button
                v-for="code in rankedExpenseCodes.recommended"
                :key="`rec-out-${code.scode}`"
                type="button"
                class="wb-acc-btn recommend"
                :class="{ on: draftExpense === code.scode, pair: code.tag === '짝' }"
                :title="code.reason || '추천'"
                @click="pickExpense(code.scode)"
              >
                <span class="wb-acc-btn-label">{{ code.sname }}</span>
                <em class="wb-rec-tag">{{ code.tag || '추천' }}</em>
              </button>
            </template>

            <p class="wb-acc-sec">전체</p>
            <button
              v-for="exp in rankedExpenseCodes.rest"
              :key="exp.scode"
              type="button"
              class="wb-acc-btn"
              :class="{ on: draftExpense === exp.scode }"
              @click="pickExpense(exp.scode)"
            >{{ exp.sname }}</button>
          </div>
        </template>

        <!-- 지출 · 상대계정만 -->
        <template v-else-if="listTab === 'out' && adjustFocus === 'rel'">
          <div class="wb-acc-head rel">
            <span class="is-in">상대계정</span>
            <div class="wb-acc-head-tools">
              <small v-if="selectedCount">수입 계정 전체</small>
              <button
                type="button"
                class="wb-gear"
                data-tour="bb-test-recommend-settings"
                @click="emit('open-match', 'rel')"
              >자동화 적용</button>
            </div>
          </div>
          <p v-if="!selectedCount" class="wb-acc-hint">왼쪽에서 지출 항목을 고르세요</p>
          <p v-else-if="!relAccounts.length" class="wb-acc-hint">수입 계정이 없습니다</p>
          <div v-else class="wb-acc-list">
            <button
              v-for="inc in relAccounts"
              :key="inc.scode"
              type="button"
              class="wb-acc-btn rel"
              :class="{ on: draftRel === inc.scode, flat: !(Number(inc.remaining) > 0) }"
              :disabled="!selectedCount"
              @click="pickRel(inc.scode)"
            >
              <span>{{ inc.sname }}</span>
              <strong>{{ formatNumber(inc.remaining) }}</strong>
            </button>
          </div>
        </template>

        <p v-else class="wb-acc-hint">이 탭에서는 사용할 수 없습니다</p>
      </aside>
    </div>

    <!-- 추천 기준 (톱니) -->
    <div v-if="settingsOpen" class="wb-settings-overlay" data-tour="bb-test-recommend-panel" @click.self="settingsOpen = false">
      <div class="wb-settings" role="dialog" aria-modal="true" aria-label="추천 기준">
        <div class="wb-settings__head">
          <h3>추천 기준</h3>
          <div class="wb-settings__head-right">
            <span
              v-if="recommendSettingSaveState === 'saving'"
              class="wb-settings__save-state"
            >저장중</span>
            <span
              v-else-if="recommendSettingSaveState === 'saved'"
              class="wb-settings__save-state ok"
            >저장됨</span>
            <span
              v-else-if="recommendSettingSaveState === 'error'"
              class="wb-settings__save-state err"
            >저장 실패</span>
            <button type="button" class="wb-settings__close" @click="settingsOpen = false">
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>
        </div>
        <div class="wb-settings__body">
          <p class="wb-settings__lead">계정 탭에 보여줄 추천만 바꿉니다. 통장에 넣는 것은 자동화입니다.</p>

          <label class="wb-settings__check">
            <input v-model="recommendSettings.enabled" type="checkbox" @change="persistRecommendSettings" />
            <span>계정 제안 사용</span>
          </label>

          <div class="wb-settings__field" :class="{ dim: !recommendSettings.enabled }">
            <span class="wb-settings__lbl">어느 적요를 볼지</span>
            <div class="wb-settings__opts">
              <button
                type="button"
                class="wb-settings__opt"
                :class="{ on: recommendSettings.scope === 'facility' }"
                :disabled="!recommendSettings.enabled"
                @click="setRecommendScope('facility')"
              >이 시설만</button>
              <button
                type="button"
                class="wb-settings__opt"
                :class="{ on: recommendSettings.scope === 'bizGubun' }"
                :disabled="!recommendSettings.enabled"
                @click="setRecommendScope('bizGubun')"
              >같은 사업</button>
              <button
                type="button"
                class="wb-settings__opt"
                :class="{ on: recommendSettings.scope === 'all' }"
                :disabled="!recommendSettings.enabled"
                @click="setRecommendScope('all')"
              >전체</button>
            </div>
          </div>

          <div class="wb-settings__actions">
            <button type="button" class="wb-settings__link" data-tour="bb-test-patterns" @click="onOpenLearn">
              <v-icon size="16">mdi-book-search-outline</v-icon>
              기억된 적요
            </button>
            <button
              v-if="isAdmin"
              type="button"
              class="wb-settings__link"
              data-tour="bb-test-pairs"
              @click="onOpenPairs"
            >
              <v-icon size="16">mdi-link-variant</v-icon>
              수입–지출 연결
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 계정 지정/수정 확인 -->
    <div v-if="confirmOpen" class="wb-confirm-overlay" @click.self="cancelConfirm">
      <div class="wb-confirm" role="dialog" aria-modal="true">
        <div class="wb-confirm__head" :class="confirmIsModify ? 'is-modify' : 'is-new'">
          <div class="wb-confirm__head-text">
            <p class="wb-confirm__kicker">{{ confirmIsModify ? '계정 수정' : '계정 지정' }}</p>
            <h3>{{ confirmTitle }}</h3>
          </div>
          <button type="button" class="wb-confirm__close" aria-label="닫기" @click="cancelConfirm">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="wb-confirm__body">
          <p>{{ confirmMessage }}</p>
          <p v-if="confirmWarning" class="wb-confirm__warn">{{ confirmWarning }}</p>
          <ul v-if="confirmDetails.length" class="wb-confirm__list">
            <li v-for="(line, idx) in confirmDetails" :key="idx">{{ line }}</li>
          </ul>
        </div>
        <div class="wb-confirm__actions">
          <button type="button" class="wb-confirm__btn ghost" @click="cancelConfirm">취소</button>
          <button type="button" class="wb-confirm__btn primary" @click="runConfirm">
            {{ confirmIsModify ? '수정하기' : '지정하기' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { bankbookApi } from '@/services/api';

export interface WorkbenchIncome {
  scode: string;
  sname: string;
  remaining: number;
  totalIncome: number;
  usedExpense: number;
  paired: boolean;
  allowedExpenses: Array<{ scode: string; sname: string }>;
}

export interface WorkbenchItem {
  cb_idx?: number;
  cb_gubun: string;
  cb_scode?: string;
  cb_rel_acc?: string;
  cb_funding?: string;
  cb_memo: string;
  cb_price: number;
  cb_date: string;
  cb_bank_code?: string;
}

type RecommendScope = 'facility' | 'bizGubun' | 'all';

interface RecommendSettings {
  enabled: boolean;
  scope: RecommendScope;
}

type RankedCode = {
  scode: string;
  sname: string;
  reason?: string;
  score: number;
  tag?: '추천' | '짝';
};

const RECOMMEND_STORAGE_KEY = 'caion.bankbook.accountRecommend';

const defaultRecommendSettings = (): RecommendSettings => ({ enabled: true, scope: 'facility' });

const loadLocalRecommendSettings = (): RecommendSettings => {
  try {
    const raw = localStorage.getItem(RECOMMEND_STORAGE_KEY);
    if (!raw) return defaultRecommendSettings();
    const parsed = JSON.parse(raw) as Partial<RecommendSettings>;
    const scope = parsed.scope === 'bizGubun' || parsed.scope === 'all' ? parsed.scope : 'facility';
    return {
      enabled: parsed.enabled !== false,
      scope,
    };
  } catch {
    return defaultRecommendSettings();
  }
};

const cacheLocalRecommendSettings = (setting: RecommendSettings) => {
  try {
    localStorage.setItem(RECOMMEND_STORAGE_KEY, JSON.stringify(setting));
  } catch {
    // ignore
  }
};

const props = withDefaults(defineProps<{
  incomes: WorkbenchIncome[];
  pendingItems: WorkbenchItem[];
  doneItems: WorkbenchItem[];
  pendingIncomes?: WorkbenchItem[];
  doneIncomes?: WorkbenchItem[];
  selectedScode: string | null;
  expenseCodes: Array<{ scode: string; sname: string }>;
  incomeCodes?: Array<{ scode: string; sname: string }>;
  ledgerItems?: WorkbenchItem[];
  bankNames: Record<string, string>;
  showBank?: boolean;
  loading?: boolean;
  hideList?: boolean;
  /** account: 계정만 / rel: 상대계정만 (지출) */
  adjustFocus?: 'account' | 'rel';
  isEditable: (item: WorkbenchItem) => boolean;
  memberCode?: string | number | null;
  bizGubun?: string | null;
  year?: string | number | null;
  isAdmin?: boolean;
}>(), {
  pendingIncomes: () => [],
  doneIncomes: () => [],
  incomeCodes: () => [],
  ledgerItems: () => [],
  hideList: false,
  adjustFocus: 'account',
  memberCode: null,
  bizGubun: null,
  year: null,
  isAdmin: false,
});

const emit = defineEmits<{
  (e: 'select-income', scode: string | null): void;
  (e: 'assign-batch', payload: { items: WorkbenchItem[]; expenseScode: string; incomeScode: string; funding?: string }): void;
  (e: 'assign-expense-account', payload: { items: WorkbenchItem[]; expenseScode: string; funding?: string }): void;
  (e: 'assign-rel', payload: { items: WorkbenchItem[]; incomeScode: string; funding?: string }): void;
  (e: 'assign-funding', payload: { items: WorkbenchItem[]; funding: string }): void;
  (e: 'unassign', item: WorkbenchItem): void;
  (e: 'assign-income', payload: { items: WorkbenchItem[]; incomeScode: string; funding?: string }): void;
  (e: 'unassign-income', item: WorkbenchItem): void;
  (e: 'selection-change', payload: {
    listTab: 'in' | 'out';
    expenseIds: number[];
    incomeIds: number[];
  }): void;
  (e: 'update:listTab', tab: 'in' | 'out'): void;
  (e: 'open-learn'): void;
  (e: 'open-pairs'): void;
  (e: 'open-match', kind: 'account' | 'rel' | 'funding'): void;
}>();

const listTab = ref<'in' | 'out'>('in');
const selectedIds = ref<number[]>([]);
const selectedIncomeIds = ref<number[]>([]);

const emitSelection = () => {
  emit('selection-change', {
    listTab: listTab.value,
    expenseIds: [...selectedIds.value],
    incomeIds: [...selectedIncomeIds.value],
  });
};
const draftExpense = ref<string | null>(null);
const draftRel = ref<string | null>(null);
const draftFunding = ref<string | null>(null);
const draftIncome = ref<string | null>(null);
/** 조정하기를 누르기 전까지 탭을 옮겨도 유지되는 선택값 */
const expenseListOpen = ref(true);
const incomeListOpen = ref(true);
const searchQuery = ref('');
const incomeSearchQuery = ref('');

const settingsOpen = ref(false);
const recommendSettings = ref<RecommendSettings>(loadLocalRecommendSettings());
const recommendSettingSaveState = ref<'idle' | 'saving' | 'saved' | 'error'>('idle');
const recommendLoading = ref(false);
const recommendMap = ref<Record<string, { score: number; reason: string }>>({});
let recommendTimer: ReturnType<typeof setTimeout> | null = null;
let recommendSeq = 0;
let recommendSaveTimer: ReturnType<typeof setTimeout> | null = null;
let recommendSaveSeq = 0;
let recommendHydratedMember: string | null = null;

const applyRecommendSettings = (setting: RecommendSettings) => {
  recommendSettings.value = {
    enabled: setting.enabled !== false,
    scope: setting.scope === 'bizGubun' || setting.scope === 'all' ? setting.scope : 'facility',
  };
  cacheLocalRecommendSettings(recommendSettings.value);
};

const loadRecommendSettingsFromDb = async () => {
  if (!props.memberCode) return;
  const key = String(props.memberCode);
  try {
    const res = await bankbookApi.getAccountRecommendSetting(props.memberCode);
    const data = res.data?.data;
    if (data && typeof data === 'object') {
      applyRecommendSettings({
        enabled: data.enabled !== false,
        scope: data.scope,
      });
      recommendHydratedMember = key;
      scheduleRecommendRefresh();
      return;
    }
  } catch (error) {
    console.error('계정 추천 설정 조회 오류:', error);
  }
  // DB에 없으면 로컬 캐시 값으로 1회 시드
  if (recommendHydratedMember !== key) {
    try {
      await bankbookApi.saveAccountRecommendSetting(props.memberCode, recommendSettings.value);
      recommendHydratedMember = key;
    } catch (error) {
      console.error('계정 추천 설정 초기 저장 오류:', error);
    }
  }
};

const persistRecommendSettings = () => {
  cacheLocalRecommendSettings(recommendSettings.value);
  scheduleRecommendRefresh();
  if (!props.memberCode) return;

  if (recommendSaveTimer) clearTimeout(recommendSaveTimer);
  recommendSettingSaveState.value = 'saving';
  const seq = ++recommendSaveSeq;
  recommendSaveTimer = setTimeout(async () => {
    try {
      const res = await bankbookApi.saveAccountRecommendSetting(props.memberCode!, {
        enabled: recommendSettings.value.enabled,
        scope: recommendSettings.value.scope,
      });
      const data = res.data?.data;
      if (data && typeof data === 'object') {
        applyRecommendSettings({
          enabled: data.enabled !== false,
          scope: data.scope,
        });
      }
      if (seq === recommendSaveSeq) {
        recommendSettingSaveState.value = 'saved';
        setTimeout(() => {
          if (recommendSettingSaveState.value === 'saved') recommendSettingSaveState.value = 'idle';
        }, 1200);
      }
    } catch (error) {
      console.error('계정 추천 설정 저장 오류:', error);
      if (seq === recommendSaveSeq) recommendSettingSaveState.value = 'error';
    }
  }, 280);
};

const setRecommendScope = (scope: RecommendScope) => {
  recommendSettings.value.scope = scope;
  persistRecommendSettings();
};

watch(
  () => props.memberCode,
  (code) => {
    recommendHydratedMember = null;
    if (code) void loadRecommendSettingsFromDb();
  },
  { immediate: true }
);

const reasonLabel = (source?: string) => {
  if (
    source === 'facility' ||
    source === 'facility_partial' ||
    source === 'exact_match' ||
    source === 'normalized_match'
  ) {
    return source === 'facility_partial' ? '이 시설 적요 유사' : '이 시설 적요 패턴';
  }
  if (
    source === 'otherFacility' ||
    source === 'otherFacility_partial' ||
    source === 'other_exact' ||
    source === 'other_normalized'
  ) {
    return source === 'otherFacility_partial' ? '다른 시설 적요 유사' : '다른 시설 적요 패턴';
  }
  if (source === 'allBizGubun' || source === 'allBizGubun_partial') {
    return source === 'allBizGubun_partial' ? '전체 적요 유사' : '전체 적요 패턴';
  }
  return '적요 패턴';
};

type ConfirmKind = 'income' | 'expense-account' | 'expense-rel';
const confirmOpen = ref(false);
const confirmKind = ref<ConfirmKind>('income');
const confirmIsModify = ref(false);
const confirmTitle = ref('');
const confirmMessage = ref('');
const confirmDetails = ref<string[]>([]);
const confirmWarning = ref('');
const pendingIncomeScode = ref<string | null>(null);
const pendingExpenseScode = ref<string | null>(null);
const pendingExpenseRelScode = ref<string | null>(null);

const dateKey = (value?: string) => (value ? String(value).slice(0, 10) : '');
const itemAmount = (item: WorkbenchItem) => Math.abs(Number(item.cb_price) || 0);

const hasScode = (item: WorkbenchItem) => !!(item.cb_scode && String(item.cb_scode).trim());
const hasRel = (item: WorkbenchItem) => !!(item.cb_rel_acc && String(item.cb_rel_acc).trim());

const byDate = (a: WorkbenchItem, b: WorkbenchItem) =>
  dateKey(a.cb_date).localeCompare(dateKey(b.cb_date));

const incomeRows = computed(() =>
  [...props.pendingIncomes, ...props.doneIncomes].sort(byDate)
);

const expenseRows = computed(() =>
  [...props.pendingItems, ...props.doneItems].sort(byDate)
);

const selectedItems = computed(() =>
  expenseRows.value.filter((item) => item.cb_idx && selectedIds.value.includes(item.cb_idx))
);

const selectedIncomeItems = computed(() =>
  incomeRows.value.filter((item) => item.cb_idx && selectedIncomeIds.value.includes(item.cb_idx))
);

const selectedCount = computed(() => selectedItems.value.length);
const selectedIncomeCount = computed(() => selectedIncomeItems.value.length);

const selectedExpenseHasScode = computed(() =>
  selectedItems.value.length > 0 && selectedItems.value.every((item) => hasScode(item))
);

const activeExpenseScode = computed(() => {
  if (props.adjustFocus === 'rel') {
    const scodes = [...new Set(
      selectedItems.value.map((item) => String(item.cb_scode || '').trim()).filter(Boolean)
    )];
    if (scodes.length === 1) return scodes[0];
    if (scodes.length > 1) return null;
  }
  return draftExpense.value;
});

const relAccounts = computed(() => {
  const map = new Map<string, WorkbenchIncome>();
  for (const inc of props.incomes) {
    const scode = String(inc?.scode || '').trim();
    if (!scode) continue;
    map.set(scode, inc);
  }
  for (const code of props.incomeCodes || []) {
    const scode = String(code?.scode || '').trim();
    if (!scode || map.has(scode)) continue;
    map.set(scode, {
      scode,
      sname: code.sname || scode,
      remaining: 0,
      totalIncome: 0,
      usedExpense: 0,
      paired: false,
      allowedExpenses: [],
    });
  }
  return [...map.values()].sort((a, b) => {
    const aHas = (Number(a.remaining) || 0) > 0 ? 1 : 0;
    const bHas = (Number(b.remaining) || 0) > 0 ? 1 : 0;
    if (aHas !== bHas) return bHas - aHas;
    if (aHas && bHas && a.remaining !== b.remaining) return b.remaining - a.remaining;
    return String(a.sname || '').localeCompare(String(b.sname || ''), 'ko');
  });
});

const visibleExpenseCodes = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  let base: Array<{ scode: string; sname: string }> = props.expenseCodes;
  // 짝으로 등록된 지출이 코드목록에 없어도 후보에 포함
  if (props.adjustFocus === 'account') {
    const uniq = new Map(base.map((c) => [c.scode, c]));
    for (const inc of props.incomes) {
      for (const exp of inc.allowedExpenses || []) {
        const scode = String(exp.scode || '').trim();
        if (!scode || uniq.has(scode)) continue;
        uniq.set(scode, { scode, sname: exp.sname || scode });
      }
    }
    base = [...uniq.values()];
  } else {
    const paired = relAccounts.value.flatMap((inc) => inc.allowedExpenses || []);
    const uniq = new Map<string, { scode: string; sname: string }>();
    for (const exp of paired) {
      if (!uniq.has(exp.scode)) uniq.set(exp.scode, exp);
    }
    base = uniq.size ? [...uniq.values()] : props.expenseCodes;
  }
  if (!q) return base;
  return base.filter((c) => c.sname.toLowerCase().includes(q) || c.scode.includes(q));
});

const visibleIncomeCodes = computed(() => {
  const q = incomeSearchQuery.value.trim().toLowerCase();
  const list = props.incomeCodes;
  if (!q) return list;
  return list.filter((c) => c.sname.toLowerCase().includes(q) || c.scode.includes(q));
});

const currentExpenseScodes = computed(() =>
  [...new Set(
    selectedItems.value
      .map((item) => String(item.cb_scode || '').trim())
      .filter(Boolean)
  )]
);

const currentIncomeScodes = computed(() =>
  [...new Set(
    selectedIncomeItems.value
      .map((item) => String(item.cb_scode || '').trim())
      .filter(Boolean)
  )]
);

/** 수입→지출 짝: 지출 계정 추천 후보 */
const pairExpenseRecommendMap = computed(() => {
  const map: Record<string, { score: number; reason: string; sname: string }> = {};
  for (const inc of props.incomes) {
    const expenses = inc.allowedExpenses || [];
    if (!expenses.length) continue;
    const remainBoost = Math.min(Math.max(Number(inc.remaining) || 0, 0) / 1_000_000, 25);
    const score = (Number(inc.remaining) > 0 ? 80 : 45) + remainBoost;
    for (const exp of expenses) {
      const scode = String(exp.scode || '').trim();
      if (!scode) continue;
      const reason = `짝 · ${inc.sname}`;
      const prev = map[scode];
      if (!prev || score > prev.score) {
        map[scode] = {
          score,
          reason,
          sname: exp.sname || scode,
        };
      } else if (prev.reason && !prev.reason.includes(inc.sname)) {
        map[scode] = {
          ...prev,
          reason: `${prev.reason} · ${inc.sname}`,
        };
      }
    }
  }
  return map;
});

const hasPairedExpenseRecommend = computed(
  () => Object.keys(pairExpenseRecommendMap.value).length > 0
);

const showExpenseRecommendSection = computed(
  () => recommendSettings.value.enabled || hasPairedExpenseRecommend.value
);

const splitRanked = (
  list: Array<{ scode: string; sname: string }>,
  currentScodes: string[],
  extraMap?: Record<string, { score: number; reason: string }>
) => {
  const currentSet = new Set(currentScodes);
  const current: RankedCode[] = [];
  const recommended: RankedCode[] = [];
  const rest: Array<{ scode: string; sname: string }> = [];

  for (const code of list) {
    if (currentSet.has(code.scode)) {
      current.push({
        scode: code.scode,
        sname: code.sname,
        reason: '현재 적용',
        score: 9999,
      });
      continue;
    }
    const hit = recommendMap.value[code.scode];
    const pair = extraMap?.[code.scode];
    if (hit || pair) {
      const score = (hit?.score || 0) + (pair?.score || 0);
      let reason = hit?.reason || '';
      if (pair?.reason) {
        reason = reason ? `${pair.reason} · ${reason}` : pair.reason;
      }
      recommended.push({
        scode: code.scode,
        sname: code.sname,
        reason: reason || '추천',
        score,
        tag: pair && !hit ? '짝' : '추천',
      });
    } else {
      rest.push(code);
    }
  }

  current.sort((a, b) => a.sname.localeCompare(b.sname, 'ko'));
  recommended.sort((a, b) => b.score - a.score || a.sname.localeCompare(b.sname, 'ko'));
  return { current, recommended, rest };
};

const rankedExpenseCodes = computed(() =>
  splitRanked(
    visibleExpenseCodes.value,
    currentExpenseScodes.value,
    pairExpenseRecommendMap.value
  )
);
const rankedIncomeCodes = computed(() =>
  splitRanked(visibleIncomeCodes.value, currentIncomeScodes.value)
);

const scheduleRecommendRefresh = () => {
  if (recommendTimer) clearTimeout(recommendTimer);
  recommendTimer = setTimeout(() => {
    void refreshRecommendations();
  }, 180);
};

const refreshRecommendations = async () => {
  const seq = ++recommendSeq;
  if (!recommendSettings.value.enabled || !props.memberCode) {
    recommendMap.value = {};
    recommendLoading.value = false;
    return;
  }

  const gubun = listTab.value === 'in' ? 'IN' : 'OUT';
  const items = gubun === 'IN' ? selectedIncomeItems.value : selectedItems.value;
  if (!items.length || props.adjustFocus === 'rel') {
    recommendMap.value = {};
    recommendLoading.value = false;
    return;
  }

  const memoMap = new Map<string, number>();
  for (const item of items) {
    const memo = String(item.cb_memo || '').trim();
    if (!memo) continue;
    memoMap.set(memo, (memoMap.get(memo) || 0) + 1);
  }
  if (!memoMap.size) {
    recommendMap.value = {};
    recommendLoading.value = false;
    return;
  }

  recommendLoading.value = true;
  const scores: Record<string, { score: number; reason: string }> = {};
  try {
    const entries = [...memoMap.entries()].slice(0, 8);
    await Promise.all(entries.map(async ([memo, weight]) => {
      try {
        const res = await bankbookApi.infer(props.memberCode!, {
          memo,
          gubun,
          bizGubun: props.bizGubun || undefined,
          scope: recommendSettings.value.scope,
          year: props.year != null ? String(props.year) : undefined,
        });
        const data = res.data?.data || res.data;
        const scode = data?.scode ? String(data.scode) : '';
        if (!scode) return;
        const count = Number(data?.count) || 1;
        const reason = reasonLabel(data?.source);
        const prev = scores[scode];
        const add = weight * (1 + Math.min(count, 20) / 10);
        if (!prev || add > prev.score) {
          scores[scode] = { score: (prev?.score || 0) + add, reason };
        } else {
          scores[scode] = { score: prev.score + add, reason: prev.reason };
        }
      } catch {
        // ignore per-memo failure
      }
    }));
    if (seq === recommendSeq) recommendMap.value = scores;
  } finally {
    if (seq === recommendSeq) recommendLoading.value = false;
  }
};

const clearFundingIfIdle = () => {
  if (!selectedIds.value.length && !selectedIncomeIds.value.length) {
    draftFunding.value = null;
  }
};

const applyFundingDraft = (items: Array<{ cb_funding?: string }>) => {
  const codes = [...new Set(items.map((item) => String(item.cb_funding || '').trim()).filter(Boolean))];
  draftFunding.value = codes.length === 1 ? codes[0] : null;
};

const syncExpenseDraftFromSelection = () => {
  const picked = expenseRows.value.filter((row) => row.cb_idx && selectedIds.value.includes(row.cb_idx));
  if (!picked.length) {
    draftExpense.value = null;
    draftRel.value = null;
    clearFundingIfIdle();
    expenseListOpen.value = true;
    return;
  }
  const scodes = [...new Set(picked.map((row) => String(row.cb_scode || '').trim()).filter(Boolean))];
  draftExpense.value = scodes.length === 1 ? scodes[0] : null;
  const rels = [...new Set(picked.map((row) => String(row.cb_rel_acc || '').trim()).filter(Boolean))];
  draftRel.value = rels.length === 1 ? rels[0] : null;
  if (listTab.value === 'out') applyFundingDraft(picked);
  expenseListOpen.value = true;
};

const adjustTargets = computed(() =>
  listTab.value === 'in' ? selectedIncomeItems.value : selectedItems.value
);

// draft는 현재 값으로 채워지므로, 「실제로 값이 달라지는지」로 변경 의도를 판정
const expenseDirty = computed(() => {
  const items = selectedItems.value;
  if (!items.length) return false;
  return !!draftExpense.value && items.some(
    (item) => String(item.cb_scode || '').trim() !== draftExpense.value
  );
});
const relDirty = computed(() => {
  const items = selectedItems.value;
  if (!items.length) return false;
  return !!draftRel.value && items.some(
    (item) => String(item.cb_rel_acc || '').trim() !== draftRel.value
  );
});
const incomeDirty = computed(() => {
  const items = selectedIncomeItems.value;
  if (!items.length) return false;
  return !!draftIncome.value && items.some(
    (item) => String(item.cb_scode || '').trim() !== draftIncome.value
  );
});
const fundingDirty = computed(() => {
  const items = adjustTargets.value;
  if (!items.length) return false;
  return !!draftFunding.value && items.some(
    (item) => String(item.cb_funding || '').trim() !== draftFunding.value
  );
});

const expenseAdjustDirty = computed(
  () => expenseDirty.value || relDirty.value || fundingDirty.value
);

const canSubmitExpenseAdjust = computed(() => {
  if (!selectedItems.value.length || !expenseAdjustDirty.value) return false;
  // 자금원천만 실제로 바뀌는 경우: 계정·상대계정 조건 없이 허용
  if (fundingDirty.value && !expenseDirty.value && !relDirty.value) return true;
  const missingRel = selectedItems.value.some((item) => !hasRel(item));
  if (relAccounts.value.length > 0 && missingRel && !draftRel.value) return false;
  if (draftExpense.value && draftRel.value) return true;
  if (draftExpense.value) return true;
  return !!draftRel.value && selectedItems.value.some((item) => hasScode(item));
});

const canSubmitAdjust = computed(() => {
  if (listTab.value === 'in') {
    return selectedIncomeItems.value.length > 0 && (incomeDirty.value || fundingDirty.value);
  }
  return canSubmitExpenseAdjust.value;
});

const pickExpense = (expenseScode: string) => {
  const items = selectedItems.value.slice();
  if (!items.length) return;
  draftExpense.value = expenseScode;
  expenseListOpen.value = true;
  const changed = items.filter((item) => String(item.cb_scode || '').trim() !== expenseScode);
  if (!changed.length) return;
  emit('assign-expense-account', { items: changed, expenseScode });
};

const pickRel = (incomeScode: string) => {
  if (!selectedItems.value.length) return;
  const items = selectedItems.value.filter((item) => hasScode(item));
  if (!items.length) return;
  draftRel.value = incomeScode;
  const changed = items.filter((item) => String(item.cb_rel_acc || '').trim() !== incomeScode);
  if (!changed.length) return;
  emit('assign-rel', { items: changed, incomeScode });
};

const pickFunding = (funding: string) => {
  const items = adjustTargets.value.slice();
  if (!items.length) return;
  draftFunding.value = funding;
  const changed = items.filter((item) => String(item.cb_funding || '').trim() !== funding);
  if (!changed.length) return;
  emit('assign-funding', { items: changed, funding });
};

const resetExpenseDraft = () => {
  selectedIds.value = [];
  draftExpense.value = null;
  draftRel.value = null;
  draftFunding.value = null;
};

const applyExpenseAdjust = () => {
  const items = selectedItems.value.slice();
  if (!items.length || !canSubmitExpenseAdjust.value) return;
  const expenseScode = expenseDirty.value ? draftExpense.value : null;
  const incomeScode = relDirty.value ? draftRel.value : null;
  const funding = fundingDirty.value ? draftFunding.value : null;
  if (expenseScode && incomeScode) {
    emit('assign-batch', { items, expenseScode, incomeScode, funding: funding || undefined });
  } else if (expenseScode) {
    emit('assign-expense-account', { items, expenseScode, funding: funding || undefined });
  } else if (incomeScode) {
    const relItems = items.filter((item) => hasScode(item));
    if (relItems.length) {
      emit('assign-rel', { items: relItems, incomeScode, funding: funding || undefined });
    }
    if (funding) {
      const fundingOnly = items.filter((item) => !hasScode(item));
      if (fundingOnly.length) emit('assign-funding', { items: fundingOnly, funding });
    }
  } else if (funding) {
    emit('assign-funding', { items, funding });
  }
  resetExpenseDraft();
  emitSelection();
  scheduleRecommendRefresh();
};

const applyIncomeAdjust = () => {
  const items = selectedIncomeItems.value.slice();
  if (!items.length || !(incomeDirty.value || fundingDirty.value)) return;
  const incomeScode = incomeDirty.value ? draftIncome.value : null;
  const funding = fundingDirty.value ? draftFunding.value : null;
  if (incomeScode) {
    emit('assign-income', { items, incomeScode, funding: funding || undefined });
  } else if (funding) {
    emit('assign-funding', { items, funding });
  }
  selectedIncomeIds.value = [];
  draftIncome.value = null;
  draftFunding.value = null;
  emitSelection();
  scheduleRecommendRefresh();
};

const applyAdjust = () => {
  if (listTab.value === 'in') {
    applyIncomeAdjust();
    return;
  }
  applyExpenseAdjust();
};

const syncIncomeDraftFromSelection = () => {
  const picked = incomeRows.value.filter((row) => row.cb_idx && selectedIncomeIds.value.includes(row.cb_idx));
  if (!picked.length) {
    draftIncome.value = null;
    clearFundingIfIdle();
    incomeListOpen.value = true;
    return;
  }
  const scodes = [...new Set(picked.map((row) => String(row.cb_scode || '').trim()).filter(Boolean))];
  draftIncome.value = scodes.length === 1 ? scodes[0] : null;
  if (listTab.value === 'in') applyFundingDraft(picked);
  incomeListOpen.value = true;
};

const formatNumber = (n: number | undefined | null) => (Number(n) || 0).toLocaleString();

const formatDate = (value: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

const expenseName = (scode?: string) => {
  if (!scode) return '없음';
  return props.expenseCodes.find((c) => c.scode === scode)?.sname
    || props.incomes.flatMap((i) => i.allowedExpenses).find((e) => e.scode === scode)?.sname
    || scode;
};

const incomeName = (scode?: string) => {
  if (!scode) return '없음';
  return props.incomes.find((i) => i.scode === scode)?.sname
    || props.incomeCodes.find((i) => i.scode === scode)?.sname
    || scode;
};

const isPicked = (item: WorkbenchItem) => !!item.cb_idx && selectedIds.value.includes(item.cb_idx);
const isIncomePicked = (item: WorkbenchItem) => !!item.cb_idx && selectedIncomeIds.value.includes(item.cb_idx);

const switchTab = (tab: 'in' | 'out') => {
  if (listTab.value !== tab) draftFunding.value = null;
  listTab.value = tab;
  emit('update:listTab', tab);
  emitSelection();
  scheduleRecommendRefresh();
};

const toggleIncomeRow = (item: WorkbenchItem) => {
  if (!item.cb_idx || !props.isEditable(item)) return;
  if (selectedIncomeIds.value.includes(item.cb_idx)) {
    selectedIncomeIds.value = selectedIncomeIds.value.filter((id) => id !== item.cb_idx);
  } else {
    selectedIncomeIds.value = [...selectedIncomeIds.value, item.cb_idx];
  }
  syncIncomeDraftFromSelection();
  emitSelection();
  scheduleRecommendRefresh();
};

const toggleRow = (item: WorkbenchItem) => {
  if (!item.cb_idx || !props.isEditable(item)) return;
  if (selectedIds.value.includes(item.cb_idx)) {
    selectedIds.value = selectedIds.value.filter((id) => id !== item.cb_idx);
  } else {
    selectedIds.value = [...selectedIds.value, item.cb_idx];
  }
  syncExpenseDraftFromSelection();
  emitSelection();
  scheduleRecommendRefresh();
};

const openConfirm = (opts: {
  kind: ConfirmKind;
  isModify: boolean;
  title: string;
  message: string;
  details: string[];
  warning?: string;
}) => {
  confirmKind.value = opts.kind;
  confirmIsModify.value = opts.isModify;
  confirmTitle.value = opts.title;
  confirmMessage.value = opts.message;
  confirmDetails.value = opts.details;
  confirmWarning.value = opts.warning || '';
  confirmOpen.value = true;
};

const cancelConfirm = () => {
  confirmOpen.value = false;
  confirmWarning.value = '';
  pendingIncomeScode.value = null;
  pendingExpenseScode.value = null;
  pendingExpenseRelScode.value = null;
};

const requestExpenseAccountAssign = (expenseScode: string) => {
  const items = selectedItems.value;
  if (!items.length) return;
  const isModify = items.some((item) => hasScode(item));
  pendingExpenseScode.value = expenseScode;
  pendingIncomeScode.value = null;
  pendingExpenseRelScode.value = null;
  openConfirm({
    kind: 'expense-account',
    isModify,
    title: isModify ? '지출 계정을 수정할까요?' : '지출 계정을 지정할까요?',
    message: isModify
      ? '이미 지정된 지출 계정을 바꿉니다. 상대계정은 유지됩니다.'
      : '선택한 지출 항목에 이 계정을 지정합니다.',
    details: [
      `지출계정 · ${expenseName(expenseScode)}`,
      `대상 ${items.length}건 · ${formatNumber(items.reduce((s, i) => s + itemAmount(i), 0))}원`,
    ],
  });
};

const requestRelAssign = (incomeScode: string) => {
  const items = selectedItems.value;
  if (!items.length || !selectedExpenseHasScode.value) return;
  const isModify = items.some((item) => hasRel(item));
  pendingExpenseRelScode.value = incomeScode;
  pendingIncomeScode.value = null;
  pendingExpenseScode.value = null;
  const expLabel = activeExpenseScode.value
    ? expenseName(activeExpenseScode.value)
    : '여러 지출계정';
  const totalAmount = items.reduce((s, i) => s + itemAmount(i), 0);
  const targetIncome = relAccounts.value.find((inc) => inc.scode === incomeScode);
  const remaining = Number(targetIncome?.remaining) || 0;
  let warning = '';
  if (remaining <= 0) {
    warning = `잔액이 없는 수입 계정입니다. 지정하면 해당 계정 잔액이 마이너스로 표시됩니다.`;
  } else if (totalAmount > remaining) {
    warning = `선택한 지출 합계(${formatNumber(totalAmount)}원)가 이 계정의 잔액(${formatNumber(remaining)}원)을 초과합니다.`;
  }
  openConfirm({
    kind: 'expense-rel',
    isModify,
    title: isModify ? '상대계정을 수정할까요?' : '상대계정을 지정할까요?',
    message: isModify
      ? '이미 지정된 상대계정을 바꿉니다.'
      : '선택한 지출 항목에 이 상대계정을 지정합니다.',
    details: [
      `지출계정 · ${expLabel}`,
      `상대계정 · ${incomeName(incomeScode)}`,
      `대상 ${items.length}건 · ${formatNumber(totalAmount)}원`,
    ],
    warning,
  });
};

const doIncomeAssign = (incomeScode: string) => {
  const items = selectedIncomeItems.value;
  if (!items.length) return;
  emit('assign-income', { items, incomeScode });
  selectedIncomeIds.value = [];
  draftIncome.value = null;
  incomeListOpen.value = true;
  emitSelection();
  scheduleRecommendRefresh();
};

const doExpenseAccountAssign = (expenseScode: string) => {
  if (!selectedItems.value.length) return;
  emit('assign-expense-account', {
    items: selectedItems.value,
    expenseScode,
  });
  selectedIds.value = [];
  draftExpense.value = null;
  expenseListOpen.value = true;
  emitSelection();
  scheduleRecommendRefresh();
};

const doRelAssign = (incomeScode: string) => {
  if (!selectedItems.value.length) return;
  emit('assign-rel', {
    items: selectedItems.value,
    incomeScode,
  });
  selectedIds.value = [];
  draftExpense.value = null;
  expenseListOpen.value = true;
  emitSelection();
};

const runConfirm = () => {
  if (confirmKind.value === 'income' && pendingIncomeScode.value) {
    doIncomeAssign(pendingIncomeScode.value);
  } else if (confirmKind.value === 'expense-account' && pendingExpenseScode.value) {
    doExpenseAccountAssign(pendingExpenseScode.value);
  } else if (confirmKind.value === 'expense-rel' && pendingExpenseRelScode.value) {
    doRelAssign(pendingExpenseRelScode.value);
  }
  cancelConfirm();
};

const onPickIncome = (incomeScode: string) => {
  const items = selectedIncomeItems.value.slice();
  if (!items.length) return;
  draftIncome.value = incomeScode;
  incomeListOpen.value = true;
  const changed = items.filter((item) => String(item.cb_scode || '').trim() !== incomeScode);
  if (!changed.length) return;
  emit('assign-income', { items: changed, incomeScode });
};

const onPickExpenseAccountOnly = (expenseScode: string) => {
  if (!selectedItems.value.length) return;
  draftExpense.value = expenseScode;
  expenseListOpen.value = true;
  requestExpenseAccountAssign(expenseScode);
};

const onOpenLearn = () => {
  settingsOpen.value = false;
  emit('open-learn');
};

const onOpenPairs = () => {
  settingsOpen.value = false;
  emit('open-pairs');
};

watch(searchQuery, (q) => {
  if (q.trim()) expenseListOpen.value = true;
});

watch(incomeSearchQuery, (q) => {
  if (q.trim()) incomeListOpen.value = true;
});

watch(draftExpense, (scode) => {
  if (!scode) expenseListOpen.value = true;
});

watch(draftIncome, (scode) => {
  if (!scode) incomeListOpen.value = true;
});

watch(
  () => props.adjustFocus,
  () => scheduleRecommendRefresh()
);

watch(
  () => expenseRows.value.map((item) => item.cb_idx),
  (ids) => {
    const valid = new Set(ids.filter((id): id is number => typeof id === 'number'));
    selectedIds.value = selectedIds.value.filter((id) => valid.has(id));
    emitSelection();
    scheduleRecommendRefresh();
  }
);

watch(
  () => incomeRows.value.map((item) => item.cb_idx),
  (ids) => {
    const valid = new Set(ids.filter((id): id is number => typeof id === 'number'));
    selectedIncomeIds.value = selectedIncomeIds.value.filter((id) => valid.has(id));
    emitSelection();
    scheduleRecommendRefresh();
  }
);

defineExpose({
  switchTab,
  toggleRow,
  toggleIncomeRow,
  isPicked,
  isIncomePicked,
  listTab,
  selectedIds,
  selectedIncomeIds,
  openSettings: () => {
    settingsOpen.value = true;
  },
  closeSettings: () => {
    settingsOpen.value = false;
  },
  setExpenseSelection: (ids: number[]) => {
    selectedIds.value = [...ids];
    syncExpenseDraftFromSelection();
    emitSelection();
    scheduleRecommendRefresh();
  },
  setIncomeSelection: (ids: number[]) => {
    selectedIncomeIds.value = [...ids];
    syncIncomeDraftFromSelection();
    emitSelection();
    scheduleRecommendRefresh();
  },
  draftExpense,
  draftRel,
  draftFunding,
  canSubmitExpenseAdjust,
  canSubmitAdjust,
  applyExpenseAdjust,
  applyAdjust,
  pickFunding,
});
</script>
